import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, MailCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, track, getSessionId } from "../lib/api";
import { useSEO, usePageView } from "../lib/seo";
import { FloatingOrbs } from "../components/motion";

const STEP_META = [
  { key: "child_age", title: "How old is your little one?", hint: "Age shapes everything — safety boundaries, what's realistic, and which support fits." },
  { key: "challenge", title: "What feels hardest right now?", hint: "Pick the one that rings truest tonight. We'll cover the rest together." },
  { key: "urgency", title: "How urgently would you like support?", hint: "There's no wrong answer — this just tells me how to pace your next step." },
  { key: "support_type", title: "What kind of support feels right?", hint: "Some parents want a map to follow solo; others want a hand to hold. Both are completely fine." },
];

export default function Assessment() {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailDone, setEmailDone] = useState(false);

  useSEO({
    title: "Free 2-Minute Sleep Assessment — The Nanny Sleep Co.",
    description: "Five gentle questions about your child's sleep — get one clear, personalised next step. No judgement, no pressure.",
  });
  usePageView("/sleep-assessment");

  useEffect(() => {
    api.assessmentConfig().then(setConfig).catch(() => toast.error("Couldn't load the assessment — please refresh."));
  }, []);

  const select = async (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (step === 0) track("assessment_start", { child_age: value });
    if (step < 3) {
      setTimeout(() => setStep(step + 1), 280);
    } else {
      setLoading(true);
      try {
        const res = await api.submitAssessment({ ...next, session_id: getSessionId() });
        setAssessmentId(res.assessment_id);
        setResult(res.recommendation);
        track("assessment_complete", next);
        track("package_recommendation", { package_id: res.recommendation.package_id, source: "assessment" });
        setStep(4);
      } catch {
        toast.error("Something hiccuped — please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please tick the consent box so I can email you.");
    setLoading(true);
    try {
      await api.captureEmail(assessmentId, { email, first_name: firstName || undefined, consent });
      track("email_capture", { source: "assessment", package_id: result?.package_id });
      setEmailDone(true);
    } catch {
      toast.error("Couldn't save your email — please check it and try again.");
    } finally {
      setLoading(false);
    }
  };

  const options = config ? [config.ages, config.challenges, config.urgency, config.support_types] : [];
  const progress = result ? (emailDone ? 100 : 90) : (step / 5) * 100;

  return (
    <div data-testid="assessment-page" className="relative overflow-hidden">
      <FloatingOrbs />
      <section className="max-w-2xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative">
        <div className="text-center mb-10">
          <p className="overline-label mb-4">Free sleep assessment</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl text-ink leading-tight">Two minutes to your<br />tailored next step</h1>
        </div>

        <div className="mb-10" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100} data-testid="assessment-progress">
          <div className="h-1.5 bg-ink/[0.07] rounded-full overflow-hidden">
            <motion.div className="h-full bg-terra rounded-full" animate={{ width: `${progress}%` }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} />
          </div>
          <p className="text-xs text-ink-muted mt-2.5 text-center">
            {result ? "Your next step is ready" : `Question ${step + 1} of 4 — then your personalised result`}
          </p>
        </div>

        <AnimatePresence mode="wait">
          {!result && config && (
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="card-soft p-7 sm:p-10"
            >
              <h2 className="font-serif text-2xl sm:text-[1.8rem] font-medium text-ink mb-2.5">{STEP_META[step].title}</h2>
              <p className="text-sm text-ink-muted mb-7">{STEP_META[step].hint}</p>
              <div className="space-y-3">
                {options[step].map((opt) => (
                  <button
                    key={opt.id}
                    data-testid={`assessment-option-${opt.id}`}
                    onClick={() => select(STEP_META[step].key, opt.id)}
                    disabled={loading}
                    className={`w-full text-left px-5 py-4 rounded-xl border text-sm sm:text-base font-medium transition-[background-color,border-color,color,transform] duration-200 active:scale-[0.99] cursor-pointer ${
                      answers[STEP_META[step].key] === opt.id
                        ? "bg-terra-light border-terra text-ink"
                        : "bg-card border-ink/10 text-ink-soft hover:border-terra/50 hover:bg-terra-light/40"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} data-testid="assessment-back" className="mt-6 inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink transition-colors cursor-pointer">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              )}
            </motion.div>
          )}

          {result && !emailDone && (
            <motion.div key="email" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="card-soft p-7 sm:p-10" data-testid="email-capture-step">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 rounded-full bg-sage-light flex items-center justify-center"><Sparkles className="w-5 h-5 text-sage" /></span>
                <h2 className="font-serif text-2xl font-medium text-ink">Your suggested next step is ready</h2>
              </div>
              <p className="text-ink-soft leading-relaxed mb-7">
                Based on what you've shared, I've put together a personalised recommendation for your family. Pop your email below and I'll show it to you straight away — and send a copy so you have it at 3am when you need it.
              </p>
              <form onSubmit={submitEmail} className="space-y-4">
                <div>
                  <label htmlFor="first-name" className="text-sm font-semibold text-ink block mb-1.5">First name <span className="font-normal text-ink-muted">(optional)</span></label>
                  <input id="first-name" data-testid="assessment-name-input" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full px-4 py-3.5 rounded-xl border border-ink/15 bg-card focus:border-terra focus:outline-none text-ink" placeholder="So I can say hello properly" />
                </div>
                <div>
                  <label htmlFor="email" className="text-sm font-semibold text-ink block mb-1.5">Email address</label>
                  <input id="email" data-testid="assessment-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3.5 rounded-xl border border-ink/15 bg-card focus:border-terra focus:outline-none text-ink" placeholder="you@example.com" />
                </div>
                <label className="flex gap-3 items-start text-sm text-ink-soft cursor-pointer">
                  <input type="checkbox" data-testid="assessment-consent-checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-[#57705F]" />
                  <span>I'm happy for The Nanny Sleep Co. to email my recommendation and occasional gentle sleep tips. No spam, unsubscribe anytime. My details are never shared or sold.</span>
                </label>
                <button type="submit" disabled={loading} data-testid="assessment-email-submit" className="btn-primary w-full disabled:opacity-70">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />}
                  Get my next step
                </button>
              </form>
              <button onClick={() => setEmailDone(true)} data-testid="assessment-skip-email" className="mt-4 text-xs text-ink-muted hover:text-ink underline underline-offset-2 transition-colors w-full text-center cursor-pointer">
                Skip — just show me my result
              </button>
            </motion.div>
          )}

          {result && emailDone && (
            <motion.div key="result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="assessment-result">
              <div className="card-soft p-7 sm:p-10 !bg-sage-light/60 border-sage/20 mb-6">
                <p className="overline-label !text-sage mb-4">Your recommended path</p>
                <h2 className="font-serif text-2xl sm:text-3xl font-medium text-ink mb-4 leading-snug">{result.headline}</h2>
                <p className="text-ink-soft leading-relaxed mb-6">{result.why}</p>
                <div className="bg-card rounded-xl p-5 mb-7 border border-ink/[0.06]">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <p className="font-semibold text-ink">{result.package.name}</p>
                    <p className="font-serif text-2xl text-ink">${result.package.price} <span className="text-sm text-ink-muted font-sans">{result.package.currency}</span></p>
                  </div>
                  <p className="text-sm text-ink-soft mt-2">{result.package.best_for}</p>
                </div>
                <Link to={result.primary_cta.href} data-testid="result-primary-cta" className="btn-primary w-full">
                  {result.primary_cta.label} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to={result.secondary_cta.href} data-testid="result-secondary-cta" className="btn-secondary w-full mt-3">
                  {result.secondary_cta.label}
                </Link>
              </div>
              <p className="text-xs text-ink-muted text-center leading-relaxed max-w-md mx-auto">
                <Check className="w-3.5 h-3.5 inline text-sage mr-1" />
                This is a general recommendation, not medical advice. If you have any health concerns about your child, please see your GP or child health nurse first.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}
